import { get as nestedGet } from 'nested-property'
import { FieldTypes } from '../constants'

import {
  Assessment,
  AssessmentSnippet,
  BaseInsuranceCoverage,
  ConfirmCoverageStatus,
  CoverageId,
  CoverageNextActions,
  CoverageWithMetadata,
  DropdownField,
  FieldMapValue,
  InsuranceCoverage,
  InsuranceCoverageHistory,
  InsuranceCoverageRequest,
  InsuranceProvider,
  isInsuranceCoverageRequest,
  isPopulatedPregnancyPlan,
  NextAction,
  PatientCoverageId,
  PatientCoverageLabel,
  PopulatedInsurancePlans,
  PopulatedPregnancyPlan,
  PopulatedPregnancyPlans,
  PregnancyCoverageLabel,
  PregnancyCoverageNextAction,
  UserInsurancePlans,
} from '../types'
// import { getHasPrimaryCoverage, getHasSecondaryCoverage } from './assessments'
import { stateNames, stateOptions } from '../constants/states'
// import { callInField, getCallInForm, optInCallInField } from '../fields/callIn/form'
import { getUniqueRandomId, parseNextActionDate } from './data'

// export const getInsuranceCallInStatus = (coverage: BaseInsuranceCoverage | undefined | null) => {
//   const isMedicaid = !!coverage?.isMedicaid
//   const hasCallInRequests = !!Object.keys(coverage?.callInRequests ?? {}).length
//   const callInRequired = !isMedicaid && hasCallInRequests
//   const callInComplete =
//     !callInRequired || !defaultStageValidate(callInField, true)(coverage ?? undefined)
//   return { callInRequired, callInComplete }
// }

export const getNewAdditionalPlanId = (
  additionalPlans: Record<string, BaseInsuranceCoverage> | undefined,
) => getUniqueRandomId(Object.keys(additionalPlans ?? {}))

export const getCoverageType = (
  id: PatientCoverageId,
  assessment: Assessment | null | undefined,
): PatientCoverageLabel => {
  if (assessment) {
    if (assessment.primaryCoverageId === id) return 'primary'
    if (assessment.secondaryCoverageId === id) return 'secondary'
    return 'additional'
  }
  if (id === 'primary') return 'primary'
  if (id === 'secondary') return 'secondary'
  return 'additional'
}
export const getCoverageHasCallIn = (
  coverage: InsuranceCoverage | undefined | null,
  assessment: Assessment | null | undefined,
  request?: InsuranceCoverageRequest,
) => {
  if (!coverage) return false
  if (coverage?.isMedicaid) return false
  if (request?.withCallInForm) return true
  const type = getCoverageType(coverage.id, assessment)
  if (type === 'primary' || type === 'secondary') return !coverage.insuranceProvider?.loqNotRequired
  if (type === 'additional') return !!Object.keys(coverage?.callInRequests ?? {}).length
  return false
}

export const getPregnancyCoverageLabel = (
  id: PatientCoverageId,
  assessment: Assessment | null,
): PregnancyCoverageLabel => {
  if (assessment?.primaryCoverageId === id) return 'primary'
  if (assessment?.secondaryCoverageId === id) return 'secondary'
  return 'potential'
}

export const getCoverageLabel = (
  id: PatientCoverageId,
  assessment?: Assessment | null,
): PregnancyCoverageLabel => {
  if (assessment !== undefined) return getPregnancyCoverageLabel(id, assessment)
  const type = getCoverageType(id, assessment)
  if (type === 'primary') return 'primary'
  if (type === 'secondary') return 'secondary'
  return 'potential'
}

export const getPregnancyCoverageId = (
  id: PatientCoverageId,
  assessment: Assessment | null,
): CoverageId | null => {
  if (!assessment) return null
  if (assessment.primaryCoverageId === id) return 'primary'
  if (assessment.secondaryCoverageId === id) return 'secondary'
  return null
}

export const getPregnancyCoveragePath = (id: PregnancyCoverageLabel) => {
  if (id === 'primary') return 'insuranceInfo.primaryCoverage'
  if (id === 'secondary') return 'insuranceInfo.secondaryCoverage'
  throw new Error(`Invalid pregnancy coverage id: ${id}`)
}

export const getCoverageComplete = (status: ConfirmCoverageStatus) => {
  const primaryComplete = !status.primaryCoverage?.incomplete?.length
  const secondaryComplete = !status.secondaryCoverage?.incomplete.length
  // const additionalComplete = !Object.keys(status.additionalPlans ?? {}).find(
  //   p => !!status.additionalPlans[p as AdditionalCoverageId]?.incomplete.length,
  // )
  return primaryComplete && secondaryComplete
}

// export const getHasMedicaidCoverage = (mergedData?: FieldMapValue) =>
//   mergedData?.insuranceInfo?.option === 'medicaid' ||
//   (mergedData?.insuranceInfo?.option === 'two-plans' &&
//     mergedData?.insuranceInfo?.onePlanIsMedicaid)

export const getEdd = (mergedData?: FieldMapValue) => mergedData?.delivery?.edd

export const getInsurancePlan = <T extends BaseInsuranceCoverage = BaseInsuranceCoverage>(
  insurancePlans: UserInsurancePlans<T>,
  id: PatientCoverageId,
): T | undefined | null => nestedGet(insurancePlans, id) ?? null

export const getCoveragePath = (id: PatientCoverageId) => {
  if (id === 'primary') return 'insurancePlans.primary'
  if (id === 'secondary') return 'insurancePlans.secondary'
  return `insurancePlans.${id}`
}

export const getCoverageStoragePath = (id: PatientCoverageId) =>
  getCoveragePath(id).replace(/\./g, '/')

export const getCoverageNextAction = (
  nextActions: CoverageNextActions,
  id: PatientCoverageId,
): NextAction | null => {
  if (id === 'primary') return nextActions.primaryCoverage ?? null
  if (id === 'secondary') return nextActions.secondaryCoverage ?? null
  return nextActions.additionalPlans?.[id.split('.')[1] ?? ''] ?? null
}

export const getPatientPlansArray = <T extends BaseInsuranceCoverage>(
  plans: UserInsurancePlans<T>,
): (T & { id: PatientCoverageId })[] => {
  const plansArray: (T & { id: PatientCoverageId })[] = []
  if (plans.primary) plansArray.push({ ...plans.primary, id: 'primary' })
  if (plans.secondary) plansArray.push({ ...plans.secondary, id: 'secondary' })
  plansArray.push(
    ...Object.entries(plans.additional ?? {})
      .filter(([, p]) => !isInsuranceCoverageRequest(p))
      .map(([id, plan]) => ({
        ...plan,
        id: `additional.${id}` as PatientCoverageId,
      })),
  )
  return plansArray
}

export const getPregnancyPlansArray = (plans: PopulatedPregnancyPlans | undefined) => {
  if (!plans) return []
  const plansArray = []
  if (plans.primary) plansArray.push(plans.primary)
  if (plans.secondary) plansArray.push(plans.secondary)
  if (plans.potential) plansArray.push(...plans.potential)
  return plansArray
}

export const getPlansInsurerIds = <T extends BaseInsuranceCoverage>(
  plans: UserInsurancePlans<T>,
): string[] => {
  const insurerIds: string[] = []
  const plansArray = getPatientPlansArray(plans)
  plansArray.forEach(p => {
    if (p.insuranceProviderId && !insurerIds.includes(p.insuranceProviderId)) {
      insurerIds.push(p.insuranceProviderId)
    }
  })
  return insurerIds
}

export const getPlanNextActionPath = (id: PatientCoverageId) => {
  if (id === 'primary') return 'nextActions.primaryCoverage'
  if (id === 'secondary') return 'nextActions.secondaryCoverage'
  return `nextActions.additionalPlans.${id.split('.')[1] ?? ''}`
}

export const getBackupPlanNextActionPath = (id: PatientCoverageId) => {
  if (id === 'primary') return 'backupNextActions.primaryCoverage'
  if (id === 'secondary') return 'backupNextActions.secondaryCoverage'
  return `backupNextActions.additionalPlans.${id.split('.')[1] ?? ''}`
}

export const getPlanNextAction = (nextActions: CoverageNextActions, id: PatientCoverageId) => {
  if (id.startsWith('additional.')) {
    const parsedId = id.split('.')[1]
    if (!parsedId) return undefined
    return nextActions?.additionalPlans?.[parsedId]
  }
  if (id === 'primary') return nextActions.primaryCoverage
  if (id === 'secondary') return nextActions.secondaryCoverage
  return undefined
}

export const getCoverageNextActions = (
  plans: PopulatedPregnancyPlans | null | undefined,
  nextActions: CoverageNextActions | null | undefined,
  backupNextActions?: CoverageNextActions | null,
): Array<PregnancyCoverageNextAction> => {
  // const { primaryIsMedicaid, medicaid, nonMedicaid, potential } = plans ?? {}
  const { primary, secondary, potential } = plans ?? {}

  const nextActionsMap: Partial<
    Record<
      PatientCoverageId,
      {
        coverage: PopulatedPregnancyPlan | undefined | null
        id: PatientCoverageId
        date: number
        nextAction: NextAction | null | undefined
        backupNextAction: NextAction | null | undefined
      }
    >
  > = {}

  const primaryCoverageId = primary?.id ?? 'primary'
  const secondaryCoverageId = secondary?.id ?? null
  nextActionsMap[primaryCoverageId] = {
    id: primaryCoverageId,
    coverage: primary,
    date: parseNextActionDate(getPlanNextAction(nextActions ?? {}, primaryCoverageId)?.text),
    nextAction: getPlanNextAction(nextActions ?? {}, primaryCoverageId) ?? null,
    backupNextAction: getPlanNextAction(backupNextActions ?? {}, primaryCoverageId) ?? null,
  }
  const secondaryNextAction = secondaryCoverageId
    ? (getPlanNextAction(nextActions ?? {}, secondaryCoverageId) ?? null)
    : null

  if (secondaryCoverageId) {
    nextActionsMap[secondaryCoverageId] = {
      id: secondaryCoverageId,
      coverage: secondary,
      date: parseNextActionDate(secondaryNextAction?.text),
      nextAction: secondaryNextAction,
      backupNextAction: getPlanNextAction(backupNextActions ?? {}, secondaryCoverageId) ?? null,
    }
  }
  // }
  if (potential) {
    potential.forEach(coverage => {
      if (!coverage?.id) return
      const nextAction = getPlanNextAction(nextActions ?? {}, coverage.id) ?? null
      nextActionsMap[coverage.id] = {
        id: coverage.id,
        coverage,
        date: parseNextActionDate(nextAction?.text),
        nextAction,
        backupNextAction: getPlanNextAction(backupNextActions ?? {}, coverage.id) ?? null,
      }
    })
  }
  return Object.values(nextActionsMap).filter(p => !!p) as PregnancyCoverageNextAction[]
}

export const getCoverageNextActionsCount = (assessment: AssessmentSnippet) => {
  let count = 1
  const { plans } = assessment ?? {}
  // const { primaryIsMedicaid, medicaid, nonMedicaid, potential } = plans ?? {}
  const { secondary, potential } = plans ?? {}
  // const secondaryCoverageId = primaryIsMedicaid ? nonMedicaid?.id : medicaid?.id
  if (secondary) count += 1
  if (potential) {
    count += potential?.length ?? 0
  }
  return count
}

export const getCoverageText = (
  coverage: BaseInsuranceCoverage | null | undefined,
  insurer: InsuranceProvider | undefined | null,
  withoutMedicaid?: boolean,
) => {
  const insuranceProviderName: string = insurer?.name || '(No Insurer)'

  const { isMedicaid } = coverage ?? {}

  const planName = coverage?.planName === "Other / I don't know" ? 'Other' : coverage?.planName
  if (isMedicaid && !withoutMedicaid) {
    if (planName && insurer?.plans?.find(p => p.name === planName)) {
      return `${insuranceProviderName} Medicaid - ${planName}`
    }
    return `${insuranceProviderName} Medicaid`
  }
  if (planName && insurer?.plans?.find(p => p.name === planName)) {
    return `${insuranceProviderName} - ${planName}`
  }
  return insuranceProviderName
}

const getPlanLabel = (id: PatientCoverageId) => {
  if (id === 'primary') return 'Primary'
  if (id === 'secondary') return 'Secondary'
  return 'Potential'
}

export const getPlanText = (
  planId: PatientCoverageId,
  coverage: BaseInsuranceCoverage | null | undefined,
  insurer: InsuranceProvider | undefined | null,
) => {
  const coverageText = getCoverageText(coverage, insurer)
  const planLabel =
    coverage && isPopulatedPregnancyPlan(coverage) ? coverage.pregnancyLabel : getPlanLabel(planId)
  return `${planLabel} - ${coverageText}`
}

export const getPatientPlansField = (
  plans: PopulatedInsurancePlans | undefined,
  optional?: boolean,
): DropdownField => {
  const plansArray = getPatientPlansArray(plans ?? {}).filter(p => !!p.insuranceProviderId)
  return {
    type: FieldTypes.DROPDOWN,
    options: plansArray.map(p => ({
      id: p.id,
      text: p ? getPlanText(p.id, p, p.insuranceProvider) : 'No coverage',
    })),
    optional: !!optional,
    placeholder: 'Select Coverage',
  }
}

export const getPregnancyPlan = (
  plans: PopulatedPregnancyPlans | undefined,
  id: PatientCoverageId,
): InsuranceCoverage | null => {
  if (!plans) return null
  // if (plans.nonMedicaid && plans.nonMedicaid.id === id) return plans.nonMedicaid
  if (plans.primary && plans.primary.id === id) return plans.primary
  if (plans.secondary && plans.secondary.id === id) return plans.secondary
  return plans.potential?.find(p => p.id === id) ?? null
}

export const getPregnancyPlansField = (
  plans: PopulatedPregnancyPlans | undefined,
  optional?: boolean,
): DropdownField => {
  const plansArray = getPregnancyPlansArray(plans).filter(p => !!p.insuranceProviderId)
  return {
    type: FieldTypes.DROPDOWN,
    options: plansArray.map(p => ({
      id: p.id,
      text: p ? getCoverageText(p, p.insuranceProvider) : 'No coverage',
    })),
    optional: !!optional,
    placeholder: 'Select Coverage',
  }
}

export const getHasMultiplePlans = (plans: UserInsurancePlans) => {
  let numPlans = 1
  const { secondary, additional } = plans
  if (secondary) numPlans += 1
  numPlans += Object.keys(additional ?? {}).length
  return numPlans > 1
}

// removes populated properties from coverage (eg insuranceProvider, id)
export const sanitizeInsuranceCoverage = ({
  insuranceProvider: _ins,
  id: _id,
  ...rest
}: InsuranceCoverage): CoverageWithMetadata => {
  const res: Partial<InsuranceCoverage> = { ...rest }
  delete res.insuranceProvider
  if (res.noMemberId) res.memberId = ''
  return res as CoverageWithMetadata
}
// }: InsuranceCoverage): CoverageWithMetadata => ({
//   ...rest,
//   memberId: rest.noMemberId ? '' : (rest.memberId ?? ''),
// })

export const addCoverageHistory = <T extends BaseInsuranceCoverage>(
  coverage: T,
  history: InsuranceCoverageHistory[],
): T => {
  const newHistory = [
    ...(coverage.history && Array.isArray(coverage.history) ? coverage.history : []),
    ...history,
  ]
  return { ...coverage, history: newHistory }
}

export const populatePregnancyPlans = (
  pregnancy: Assessment | undefined | null,
  patientPlans: PopulatedInsurancePlans | undefined | null,
): PopulatedPregnancyPlans => {
  if (!pregnancy) return { primary: null, secondary: null, potential: [] }
  const plansArray = getPatientPlansArray(patientPlans ?? {})
  const { primaryCoverageId, secondaryCoverageId, potentialCoverageIds } = pregnancy

  const primaryBasePlan = plansArray.find(p => p.id === primaryCoverageId) ?? null
  const secondaryBasePlan = plansArray.find(p => p.id === secondaryCoverageId) ?? null
  const primaryPlan: PopulatedPregnancyPlan | null = primaryBasePlan
    ? { ...primaryBasePlan, pregnancyLabel: 'primary' }
    : null
  const secondaryPlan: PopulatedPregnancyPlan | null = secondaryBasePlan
    ? { ...secondaryBasePlan, pregnancyLabel: 'secondary' }
    : null
  const potentialPlans =
    potentialCoverageIds?.reduce<Array<PopulatedPregnancyPlan>>((acc, id) => {
      const plan = plansArray.find(p => p.id === id)
      if (plan) acc.push({ ...plan, pregnancyLabel: 'potential' })
      return acc
    }, []) ?? []

  const plans: PopulatedPregnancyPlans = {
    primary: primaryPlan ?? null,
    secondary: secondaryPlan ?? null,
    potential: potentialPlans,
  }
  return plans
}

export const pregnancyHasPlan = (
  pregnancy: Assessment | undefined | null,
  id: PatientCoverageId,
) => {
  if (!pregnancy) return false
  if (pregnancy.primaryCoverageId === id || pregnancy.secondaryCoverageId === id) return true
  return !!pregnancy.potentialCoverageIds?.includes(id)
}

const getStateAcronym = (state: string) =>
  stateOptions.find(s => state.toLowerCase() === s.text.toLowerCase())?.id

export const getPlanState = (coverage: BaseInsuranceCoverage | null | undefined) => {
  const planStateValue = coverage?.['plan-design-and-state-mandates']?.followsStateMandatesNote
  if (!planStateValue) return null
  const completeAcronymMatch = stateNames[planStateValue]
  if (completeAcronymMatch) return completeAcronymMatch

  const fullStateMatch = stateOptions
    .map(s => s.text.toLowerCase())
    .find(s => planStateValue?.toLowerCase().includes(s))
  const fullStateMatchAcronym = fullStateMatch ? getStateAcronym(fullStateMatch) : null
  if (fullStateMatchAcronym) return fullStateMatchAcronym

  // get value without punctuation
  const stateValueNoPunctuation = planStateValue?.replace(/[^a-zA-Z]/g, '')
  const stateAcronymMatch = stateOptions.find(s =>
    stateValueNoPunctuation?.toLowerCase().split(' ').includes(s.text.toLowerCase()),
  )?.id

  return stateAcronymMatch ?? null
}
